import React from 'react';
import { HiExclamationTriangle, HiInformationCircle, HiCheckCircle } from 'react-icons/hi2';
import { Modal, ModalBody, ModalFooter } from './Modal';
import { VBButton } from './VBButton';

export type ConfirmDialogVariant = 'danger' | 'warning' | 'info' | 'success';

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
  isLoading?: boolean;
}

const variantConfig: Record<
  ConfirmDialogVariant,
  {
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
    buttonVariant: 'primary' | 'danger';
  }
> = {
  danger: {
    icon: <HiExclamationTriangle className="w-6 h-6" />,
    iconBg: 'bg-vb-danger/10',
    iconColor: 'text-vb-danger',
    buttonVariant: 'danger'
  },
  warning: {
    icon: <HiExclamationTriangle className="w-6 h-6" />,
    iconBg: 'bg-yellow-500/10',
    iconColor: 'text-yellow-500',
    buttonVariant: 'primary'
  },
  info: {
    icon: <HiInformationCircle className="w-6 h-6" />,
    iconBg: 'bg-vb-primary/10',
    iconColor: 'text-vb-primary',
    buttonVariant: 'primary'
  },
  success: {
    icon: <HiCheckCircle className="w-6 h-6" />,
    iconBg: 'bg-vb-success/10',
    iconColor: 'text-vb-success',
    buttonVariant: 'primary'
  }
};

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  isLoading = false
}) => {
  const config = variantConfig[variant];

  // Whether a confirm click is awaiting its close. The close decision cannot be
  // made synchronously in the click handler: that render's `isLoading` is stale
  // by definition — the confirm button is disabled while loading, so at click
  // time the captured value is always false (which used to close the dialog
  // instantly and made the "Please wait..." state unreachable).
  const [confirmPending, setConfirmPending] = React.useState(false);

  const handleConfirm = () => {
    setConfirmPending(true);
    onConfirm();
  };

  // Close after a confirm once the caller is (no longer) loading. Sync callers
  // (isLoading never set) close on the effect flush right after the click —
  // same observable behavior as before. Async callers keep the dialog open,
  // showing "Please wait...", until their request settles; a caller that also
  // closes explicitly (e.g. in a finally block) just wins the race harmlessly.
  React.useEffect(() => {
    if (!confirmPending || isLoading) return;
    setConfirmPending(false);
    if (isOpen) onClose();
  }, [confirmPending, isLoading, isOpen, onClose]);

  // User-initiated dismissal (Escape, overlay click, Cancel) must not interrupt
  // an in-flight confirm: the buttons are already disabled while loading, so
  // guard the modal's dismissal paths the same way. The completion effect above
  // still calls the real onClose once the action settles.
  const handleUserClose = () => {
    if (!isLoading) onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleUserClose} closeOnOverlayClick={!isLoading} size="sm">
      <ModalBody>
        <div className="flex flex-col items-center text-center py-4">
          {/* Icon */}
          <div
            className={`w-14 h-14 rounded-full ${config.iconBg} ${config.iconColor} flex items-center justify-center mb-4`}
          >
            {config.icon}
          </div>

          {/* Title */}
          <h3 className="text-lg font-semibold text-vb-text-primary dark:text-vb-text-primary-dark mb-2">
            {title}
          </h3>

          {/* Message */}
          <p className="text-sm text-vb-text-secondary dark:text-vb-text-secondary-dark">
            {message}
          </p>
        </div>
      </ModalBody>

      <ModalFooter className="!justify-center gap-3">
        <VBButton variant="secondary" size="md" onClick={handleUserClose} disabled={isLoading}>
          {cancelText}
        </VBButton>
        <VBButton
          variant={config.buttonVariant}
          size="md"
          onClick={handleConfirm}
          disabled={isLoading}
        >
          {isLoading ? 'Please wait...' : confirmText}
        </VBButton>
      </ModalFooter>
    </Modal>
  );
};

// Hook for easier usage
export interface UseConfirmDialogOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: ConfirmDialogVariant;
}

export interface UseConfirmDialogReturn {
  isOpen: boolean;
  openDialog: () => void;
  closeDialog: () => void;
  ConfirmDialogComponent: React.FC<{ onConfirm: () => void }>;
}

export const useConfirmDialog = (options: UseConfirmDialogOptions): UseConfirmDialogReturn => {
  const [isOpen, setIsOpen] = React.useState(false);

  const openDialog = () => setIsOpen(true);
  const closeDialog = () => setIsOpen(false);

  const ConfirmDialogComponent: React.FC<{ onConfirm: () => void }> = ({ onConfirm }) => (
    <ConfirmDialog isOpen={isOpen} onClose={closeDialog} onConfirm={onConfirm} {...options} />
  );

  return {
    isOpen,
    openDialog,
    closeDialog,
    ConfirmDialogComponent
  };
};
